import challengeIcon from "@/assets/challenge.png";
import { MetaLayout } from "@/common/MetaLayout";
import Outlet from "@/common/Outlet";
import EventFilters from "@/components/EventFilters";
import { schema } from "@/components/EventFilters/schema";
import { Container } from "@/components/Container";
import { SECTIONS } from "@/components/EventsSection";
import { HeaderContainer } from "@/components/HeaderContainer";
import NavItem from "@/components/NavItem";
import { ScrollableScaffoldNav } from "@/components/ScrollableScaffoldNav";
import { Badge } from "@/kit/ui/badge";
import { Link } from "@/kit/ui/link";
import { useLoginRedirect } from "@/utils/auth";
import { EVENT_ORDER } from "@/utils/eventOrder";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import { parseRequestSearch, useValibotSearchParams } from "@/utils/validation";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { Environment, graphql } from "react-relay";
import {
  ActivityScaffoldQuery as ActivityScaffoldQueryType,
  EventQueryFilters,
} from "./__generated__/ActivityScaffoldQuery.graphql";

export const GETTING_STARTED_TAG: string = "Tutorial" as const;

const toFilters = (filters: EventQueryFilters | null | undefined) => ({
  search: filters?.search,
  tags: filters?.tags ? [...filters.tags] : undefined,
  rewards: filters?.rewards ? [...filters.rewards] : undefined,
  location: filters?.locations?.[0] ?? undefined,
});

export default function EventScaffold() {
  const { variables, query } = useEventScaffoldQuery();
  const [_, setSearchParams] = useValibotSearchParams(schema);
  const loginRedirect = useLoginRedirect();
  return (
    <MetaLayout>
      <HeaderContainer
        subSection={
          <>
            <EventFilters
              query={query}
              onChangeFilters={setSearchParams}
              filters={toFilters(variables.filters)}
              withFilters
            />
            <ScrollableScaffoldNav className="-mb-px">
              <div className="flex justify-start items-center w-full">
                <NavItem to="/challenges" end>
                  <FormattedMessage defaultMessage="All" />
                  <Badge size="sm" shape="pill" variant="secondary">
                    <FormattedNumber
                      value={query.events.totalCount}
                      notation="compact"
                    />
                  </Badge>
                </NavItem>

                {SECTIONS.map(({ key, to, label }) => (
                  <NavItem key={key} to={to} end>
                    {label}
                    <Badge size="sm" shape="pill" variant="secondary">
                      <FormattedNumber
                        value={query[key].totalCount}
                        notation="compact"
                      />
                    </Badge>
                  </NavItem>
                ))}

                {query.viewer ? (
                  <NavItem to="/challenges/mine" end>
                    <FormattedMessage defaultMessage="My Challenges" />
                    {query.viewer.events && (
                      <Badge size="sm" shape="pill" variant="secondary">
                        <FormattedNumber
                          value={query.viewer.events.totalCount}
                          notation="compact"
                        />
                      </Badge>
                    )}
                  </NavItem>
                ) : (
                  <NavItem to={loginRedirect} rel="nofollow" end>
                    <FormattedMessage defaultMessage="My Challenges" />
                  </NavItem>
                )}
              </div>
            </ScrollableScaffoldNav>
          </>
        }
      >
        <div className="w-full flex md:flex-row flex-col md:gap-0 gap-6 justify-between items-start">
          <div className="flex w-full justify-start items-center gap-3">
            <img src={challengeIcon} className="w-20 h-20" />
            <div className="flex flex-col justify-around gap-1">
              <h1 className="text-3xl font-semibold text-grey-900 leading-9">
                <FormattedMessage defaultMessage="Quantum challenges" />
              </h1>
              <h2 className="text-base font-normal text-grey-600 leading-6">
                <FormattedMessage defaultMessage="Find a challenge to join, or create a challenge." />
              </h2>
            </div>
          </div>
          {query.viewer && query.viewer.viewerCanCreateEvent && (
            <Link
              variant="primary"
              to="/challenges/new"
              className="sm:w-auto w-full"
            >
              <FormattedMessage defaultMessage="Host Challenge" />
            </Link>
          )}
        </div>
      </HeaderContainer>
      <Container>
        <Outlet />
      </Container>
    </MetaLayout>
  );
}

const EventScaffoldQuery = graphql`
  query ActivityScaffoldQuery(
    $search: String
    $filters: EventQueryFilters
    $tutorialFilters: EventQueryFilters
    $upcomingFilters: EventQueryFilters
    $activeFilters: EventQueryFilters
    $closedFilters: EventQueryFilters
  ) {
    competitions(first: 0, filters: { search: $search }) {
      totalCount
      edges {
        cursor
      }
    }
    viewer @ifAllowed {
      events(first: 0) {
        totalCount
      }
      viewerCanCreateEvent: viewerCan(action: CREATE_EVENT)
    }
    events(first: 0, filters: $filters) {
      totalCount
    }
    tutorial: events(first: 0, filters: $tutorialFilters) {
      totalCount
    }
    upcoming: events(first: 0, filters: $upcomingFilters) {
      totalCount
    }
    active: events(first: 0, filters: $activeFilters) {
      totalCount
    }
    closed: events(first: 0, filters: $closedFilters) {
      totalCount
    }
    ...EventFiltersFragment @arguments(filters: $filters)
  }
`;

const withBase = (
  base: EventQueryFilters,
  overrides: Partial<EventQueryFilters>,
): EventQueryFilters => ({
  ...base,
  ...overrides,
});

export const loader =
  (environment: Environment) =>
  ({ request }: LoaderArgs) => {
    const { search, sort, ...rest } = parseRequestSearch(schema, request);
    const { order, direction: sortDirection } = EVENT_ORDER.values[sort];
    const baseFilters = { order, sortDirection, ...rest };

    return preload<ActivityScaffoldQueryType>(environment, EventScaffoldQuery, {
      search,
      filters: baseFilters,
      tutorialFilters: withBase(baseFilters, {
        tags: [GETTING_STARTED_TAG, ...(baseFilters.tags ?? [])],
      }),
      upcomingFilters: withBase(baseFilters, { status: "UPCOMING" }),
      activeFilters: withBase(baseFilters, { status: "ACTIVE" }),
      closedFilters: withBase(baseFilters, { status: "CLOSED" }),
    });
  };

const useEventScaffoldQuery = () => usePreloaded<ActivityScaffoldQueryType>();
